import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS, createLazyQuery } from '@/shared/api';
import { ChangePasswordRequest, ProfileData, UpdateProfileRequest, UpdateProfileResponse } from '../types';
import { changeMyPassword, fetchMyProfile, updateMyProfile, updateUiSettings } from './api';

export const profileKeys = {
  all: [API_TAGS.PROFILE] as const,
  details: () => [...profileKeys.all, 'detail'] as const,
  me: () => [...profileKeys.details(), 'me'] as const,
};

export const useMyProfile = () => {
  return useQuery<ProfileData>({
    queryKey: profileKeys.me(),
    queryFn: fetchMyProfile,
  });
};
/**
 * Хук для ленивой загрузки профиля текущего пользователя (ручной триггер).
 */
export const useLazyMyProfile = createLazyQuery<void, ProfileData>(
  () => profileKeys.me(),
  () => fetchMyProfile(),
  {
    mode: 'cached',
  },
);

export const useChangePassword = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, ChangePasswordRequest>({
    mutationFn: changeMyPassword,
    onSuccess: () => {
      // no specific cache to invalidate, but we can refetch profile just in case
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });
};

export const useUpdateMyProfile = () => {
  const queryClient = useQueryClient();
  return useMutation<UpdateProfileResponse, unknown, UpdateProfileRequest>({
    mutationFn: updateMyProfile,
    onSuccess: (data) => {
      // Update the cached me profile optimistically with returned data
      queryClient.setQueryData(profileKeys.me(), (prev: any) => ({ ...(prev || {}), ...data }));
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });
};

/**
 * Хук для сохранения настроек интерфейса пользователя на бэкенде.
 * Ошибки логируются в консоль, но не показываются пользователю.
 */
export const useUpdateUiSettings = () => {
  return useMutation({
    mutationFn: updateUiSettings,
    onError: (error: unknown) => {
      console.error('Ошибка сохранения настроек интерфейса:', error);
    },
  });
};
